/**
 * The one place this feature talks to `anudan/v1` (`05-API-CONTRACT.md` §5.4).
 *
 * Every figure a donor sees is computed server-side (A-1) — this module never adds, multiplies
 * or rounds a monetary value, it only relays what the server already computed. `restUrl` comes
 * from `window.AnudanEngine.config`, which `checkout.js` reads from the JSON block
 * `Frontend\CheckoutAssets` prints, per the single-global rule (`07-CODE-STANDARDS.md` §7.1).
 */

/**
 * 0.9.41-rc1: a total the server has not given in this long is given up on, answered as no reply. Continue on the gift step waits
 * for the total with no limit of its own (it says "Still working…" after four seconds), so this is what ends a wait for a server
 * that never answers: the donor is told the total could not be worked out and can try again (decisions log, 2026-10-29).
 */
const QUOTE_REQUEST_LIMIT_MS = 20000;

/**
 * Returns the configured REST root, with a same-origin fallback so a page that somehow loaded
 * this bundle without the inline config script still fails into something reachable rather than
 * into `undefined/…`.
 *
 * @return {string}
 */
function restRoot() {
	const config = window.AnudanEngine && window.AnudanEngine.config;

	return ( config && config.restUrl ) || '/wp-json/anudan/v1/';
}

/**
 * Issues one request and returns the parsed body, whether the response was ok or not — every
 * route in §5.4 answers with a structured body even on failure (§5.3's envelope), so the caller
 * always has `code`/`message`/`data` to read rather than having to branch on the transport layer.
 *
 * @param {string} path
 * @param {RequestInit} [init]
 * @param {number} [limitMs] Gives up after this long, answered as no reply (status 0); none when 0.
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
async function request( path, init, limitMs = 0 ) {
	const controller = limitMs > 0 && 'function' === typeof AbortController ? new AbortController() : null;
	const timer = controller ? setTimeout( () => controller.abort(), limitMs ) : null;

	try {
		return await send( path, controller ? { ...init, signal: controller.signal } : init );
	} finally {
		if ( timer ) {
			clearTimeout( timer );
		}
	}
}

/**
 * One request and its parsed body ({@see request}).
 *
 * @param {string} path
 * @param {RequestInit} [init]
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
async function send( path, init ) {
	let response;

	try {
		/*
		 * 0.9.41: the JSON type only with a body. A GET sent with it no longer matched the form schema's preload in Firefox, which
		 * then fetched the schema a second time (R2-13 on Firefox); a GET has no body for the header to describe.
		 */
		const typed = init && undefined !== init.body ? { 'Content-Type': 'application/json' } : {};

		response = await fetch( restRoot() + path, {
			...init,
			headers: { ...typed, ...( init && init.headers ) },
		} );
	} catch ( error ) {
		// No answer at all: the connection dropped. Status 0, so a caller can tell "no reply"
		// from "refused" and never says "you have not been charged" when it cannot know
		// (release gate 0.5.3, adversarial review round 6, MEDIUM-2). Never thrown: a rejected
		// poll used to stop polling for good.
		return { ok: false, status: 0, body: null };
	}

	let body = null;

	try {
		body = await response.json();
	} catch ( error ) {
		body = null;
	}

	return { ok: response.ok, status: response.status, body };
}

/**
 * Whether this site answers at all, asked with one small request that is never cached. Any answer counts, a refusal included;
 * only no answer, or none within four seconds, is false. For telling a dropped connection from anything else when the browser's own
 * `navigator.onLine` has not caught up yet (WebKit reports going offline a moment late, PHN-12).
 *
 * @return {Promise<boolean>}
 */
export async function siteAnswers() {
	const controller = 'function' === typeof AbortController ? new AbortController() : null;
	const timer = controller ? setTimeout( () => controller.abort(), 4000 ) : null;

	try {
		await fetch( restRoot(), { method: 'HEAD', cache: 'no-store', signal: controller ? controller.signal : undefined } );

		return true;
	} catch ( error ) {
		return false;
	} finally {
		if ( timer ) {
			clearTimeout( timer );
		}
	}
}

/**
 * Turns any failed response into one plain sentence a donor can act on: what happened, and what
 * to do next. Never empty, never a code, never "Something went wrong".
 *
 * Staging 0.5.4: pressing Give got a 429 and the donor saw nothing at all, because the only text
 * written anywhere was inside a visually hidden live region. Every failure now goes through this
 * and is shown in the visible message above the buttons.
 *
 * - **No answer at all (status 0)**: the connection dropped or the site is unreachable.
 * - **429**: too many attempts; says how long to wait when the server said.
 * - **Any other refusal with the server's own sentence**: that sentence, which is already
 *   translated and donor-safe (`Rest\ErrorEnvelope`).
 * - **A 5xx with no sentence** (a proxy's error page, a PHP fatal): the site had a problem.
 *
 * @param {{ok: boolean, status: number, body: any}} response
 * @param {string} [fallback] Used when nothing more specific applies.
 * @return {string}
 */
export function describeFailure( response, fallback ) {
	const __ = ( text ) => ( window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text );
	const said = describeWithoutReference( response, fallback, __ );
	const data = response && response.body && response.body.data;
	const reference = data && 'string' === typeof data.reference && /^[A-Z0-9]{6}$/.test( data.reference ) ? data.reference : '';

	// After 0.9.41-rc7: a refusal's short reference, which is also on its line in the event log.
	return reference ? `${ said } ${ __( 'Reference: %s' ).replace( '%s', reference ) }` : said;
}

/**
 * {@see describeFailure} without the reference.
 *
 * @param {{ok: boolean, status: number, body: any}} response
 * @param {string|undefined} fallback
 * @param {(text: string) => string} __
 * @return {string}
 */
function describeWithoutReference( response, fallback, __ ) {
	const status = response && 'number' === typeof response.status ? response.status : 0;
	const body = response ? response.body : null;
	const serverMessage = body && 'string' === typeof body.message ? body.message.trim() : '';

	if ( 0 === status ) {
		return __( 'We could not reach this site. Please check your internet connection and try again. You have not been charged.' );
	}

	if ( 429 === status ) {
		const wait = body && body.data ? Number( body.data.retry_after ) : 0;
		let when = __( 'a moment' );

		if ( wait >= 90 ) {
			when = __( '%s minutes' ).replace( '%s', String( Math.ceil( wait / 60 ) ) );
		} else if ( wait > 0 ) {
			when = __( '%s seconds' ).replace( '%s', String( Math.ceil( wait ) ) );
		}

		// No button named: a limit can answer any step's request, and each step's button differs (0.9.40 Phase 4).
		return __( 'There have been too many attempts from this connection. Please wait %s, then try again. You have not been charged.' ).replace( '%s', when );
	}

	if ( serverMessage ) {
		return serverMessage;
	}

	if ( status >= 500 ) {
		return __( 'The site had a problem and could not start your donation. You have not been charged. Please try again in a few minutes.' );
	}

	return fallback || __( 'Your donation could not be started. You have not been charged. Please check your details and try again.' );
}

/**
 * `GET /funds/{id}/form`.
 *
 * @param {number} fundId
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function getForm( fundId ) {
	return request( `funds/${ encodeURIComponent( String( fundId ) ) }/form`, { method: 'GET' } );
}

/**
 * `GET /cart/form` (0.9.0, the gift cart).
 *
 * @param {string} fundIds Comma-separated fund ids the page offers, or empty for every fund.
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function getCartForm( fundIds ) {
	const query = fundIds ? `?funds=${ encodeURIComponent( fundIds ) }` : '';

	return request( `cart/form${ query }`, { method: 'GET' } );
}

/**
 * `POST /cart/quote` — the cart's figures, every one computed by the server (A-1).
 *
 * @param {object} payload
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function cartQuote( payload ) {
	return request( 'cart/quote', { method: 'POST', body: JSON.stringify( payload ) }, QUOTE_REQUEST_LIMIT_MS );
}

/**
 * `POST /cart/intent`.
 *
 * @param {object} payload
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function cartIntent( payload ) {
	return request( 'cart/intent', { method: 'POST', body: JSON.stringify( payload ) } );
}

/**
 * `POST /checkout/quote`.
 *
 * @param {object} payload
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export async function quote( payload ) {
	const nonce = window.AnudanEngine && window.AnudanEngine.config && window.AnudanEngine.config.restNonce;
	const init = { method: 'POST', body: JSON.stringify( payload ) };

	if ( ! nonce ) {
		return request( 'checkout/quote', init, QUOTE_REQUEST_LIMIT_MS );
	}

	// Only the quote carries the signed-in donor's nonce, so the server can tell a donor which months they already gave for (it is told
	// to nobody else). A nonce that has gone stale makes WordPress refuse the whole request; the quote is then asked again as a visitor.
	const signed = await request( 'checkout/quote', { ...init, headers: { 'X-WP-Nonce': nonce } }, QUOTE_REQUEST_LIMIT_MS );

	return 403 === signed.status ? request( 'checkout/quote', init, QUOTE_REQUEST_LIMIT_MS ) : signed;
}

/**
 * BACKLOG 31 (after 0.9.41-rc5): how long the page waits for the email check before it lets it go. The donor is never waiting on it:
 * it runs as they leave the email field, and nothing waits for its answer.
 */
export const EMAIL_CHECK_LIMIT_MS = 1500;

/**
 * `POST /checkout/email` — the donor's address checked as they leave the email field, so its domain's answer is kept for the final
 * check. Answered as no reply (status 0) after {@see EMAIL_CHECK_LIMIT_MS}.
 *
 * @param {string} email
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function checkEmail( email ) {
	return request( 'checkout/email', { method: 'POST', body: JSON.stringify( { email } ) }, EMAIL_CHECK_LIMIT_MS );
}

/**
 * `POST /checkout/intent`.
 *
 * @param {object} payload
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function intent( payload ) {
	return request( 'checkout/intent', { method: 'POST', body: JSON.stringify( payload ) } );
}

/**
 * `POST /checkout/confirm`.
 *
 * @param {string} donationUid
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function confirm( donationUid ) {
	return request( 'checkout/confirm', {
		method: 'POST',
		body: JSON.stringify( { donation_uid: donationUid } ),
	} );
}

/**
 * `POST /checkout/confirm` with a `problem`: tells the site a payment did not go through as expected,
 * so it is in the event log and not only on the donor's screen (0.9.1). Fire and forget: what the
 * donor is told never waits on it. `problem` is one of the server's own list; `code` is a short slug.
 * It changes nothing about the donation.
 *
 * @param {string} donationUid
 * @param {string} problem
 * @param {string} [code]
 * @return {void}
 */
export function reportProblem( donationUid, problem, code = '' ) {
	request( 'checkout/confirm', {
		method: 'POST',
		body: JSON.stringify( { donation_uid: donationUid, problem, code } ),
	} );
}

/**
 * `POST /checkout/stalled` — tells the site which step a donor's checkout stopped waiting at, when Give's own time limit ended
 * it (0.9.38). The step is one of a fixed list, the gateway `stripe`, `paypal` or empty; nothing the donor typed and no amount
 * is sent. Fire and forget: a report that does not arrive changes nothing for the donor.
 *
 * @param {string} step
 * @param {string} gateway
 * @param {string} [detail] After rc5: why the security check gave up (Cloudflare's error code, or `script`, `timeout`).
 * @return {void}
 */
export function reportStall( step, gateway, detail = '' ) {
	const config = window.AnudanEngine && window.AnudanEngine.config;

	try {
		fetch( restRoot() + 'checkout/stalled', {
			method: 'POST',
			keepalive: true,
			// A signed-in donor's stall nonce was made for their account. Without the REST nonce WordPress treats the request as
			// anonymous, the stall nonce no longer matches and the report is refused.
			headers: { 'Content-Type': 'application/json', ...( config && config.restNonce ? { 'X-WP-Nonce': config.restNonce } : {} ) },
			body: JSON.stringify( {
				step,
				gateway: 'stripe' === gateway || 'paypal' === gateway ? gateway : '',
				detail: String( detail || '' ),
				nonce: ( config && config.stallNonce ) || '',
			} ),
		} ).catch( () => {} );
	} catch ( error ) {
		// Nothing to do: the donor has already been told.
	}
}

/**
 * `POST /checkout/paypal-capture` — asks the server to capture the PayPal order the donor has
 * just approved. The server checks the order is this donation's before moving any money, and the
 * donation still completes only when PayPal's webhook says so (CLAUDE.md rule 6).
 *
 * @param {string} donationUid
 * @param {string} orderId
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function capturePayPal( donationUid, orderId ) {
	return request( 'checkout/paypal-capture', {
		method: 'POST',
		body: JSON.stringify( { donation_uid: donationUid, order_id: orderId } ),
	} );
}

/**
 * `GET /checkout/status/{uid}` — WP-18's revisit of §5.4's deferral, updated in WP-24c: what
 * unlocks donor details here is no longer a `session_id` this module sends explicitly. It is
 * the `HttpOnly` cookie `POST /checkout/intent`'s response set, which `fetch()`'s own default
 * `credentials: 'same-origin'` already attaches to this same-origin request without this module
 * ever reading or holding a copy of the value — the whole point (`05-API-CONTRACT.md` §5.4).
 *
 * @param {string} donationUid
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function status( donationUid ) {
	return request( `checkout/status/${ encodeURIComponent( donationUid ) }`, { method: 'GET' } );
}

/**
 * `GET /restart/{token}` — what a personal link to move a PayPal gift here fills in (0.9.12).
 *
 * @param {string} token
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function restart( token ) {
	return request( `restart/${ encodeURIComponent( token ) }`, { method: 'GET' } );
}

/**
 * `POST /restart/{token}/confirmed` — the donor finished; the server asks the gateway itself (0.9.12).
 *
 * @param {string} token
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
export function restartConfirmed( token ) {
	return request( `restart/${ encodeURIComponent( token ) }/confirmed`, { method: 'POST', body: '{}' } );
}
